import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon, FunnelIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import apiService, { AuditEvent, AuditQueryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';

const AUDIT_PAGE_LIMIT = 100;

type TimeRangeUnit = 'minutes' | 'hours' | 'days' | 'weeks';

const TIME_RANGE_AMOUNTS = [1, 2, 3, 5, 6, 10, 12, 15, 20, 24, 30, 45, 60, 90, 120];
const TIME_RANGE_UNITS: { value: TimeRangeUnit; label: string }[] = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
  { value: 'weeks', label: 'weeks' },
];

function amountToMinutes(amount: number, unit: TimeRangeUnit): number {
  switch (unit) {
    case 'minutes': return amount;
    case 'hours':   return amount * 60;
    case 'days':    return amount * 24 * 60;
    case 'weeks':   return amount * 7 * 24 * 60;
    default:        return amount;
  }
}

function getTimeRangeBounds(amount: number, unit: TimeRangeUnit): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getTime() - amountToMinutes(amount, unit) * 60_000);
  const fmt = (d: Date) => d.toISOString().replace('T', ' ').slice(0, 19);
  return { from: fmt(from), to: fmt(now) };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Parse a UTC timestamp string (with or without Z) into a local-time display string with UTC offset. */
function fmtDt(iso: string): string {
  if (!iso) return '—';
  const utcIso = /[Z+]/.test(iso) ? iso : iso.replace(' ', 'T') + 'Z';
  const d = new Date(utcIso);
  const pad = (n: number) => String(n).padStart(2, '0');
  const off = d.getTimezoneOffset();
  const sign = off <= 0 ? '+' : '-';
  const absH = Math.floor(Math.abs(off) / 60);
  const absM = Math.abs(off) % 60;
  const tz = absM === 0 ? `UTC${sign}${absH}` : `UTC${sign}${absH}:${pad(absM)}`;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
    + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${tz}`;
}

function fmtMs(ms: number): string {
  return fmtDt(new Date(ms).toISOString());
}

/** Parse BetterStack dt string to ms (treating it as UTC). */
function dtToMs(dt: string): number {
  if (!dt) return 0;
  const utc = /[Z+]/.test(dt) ? dt : dt.replace(' ', 'T') + 'Z';
  return new Date(utc).getTime();
}

function levelBadge(level: string) {
  const base = 'inline-block px-1.5 py-0.5 rounded text-xs font-medium';
  switch ((level ?? '').toLowerCase()) {
    case 'error':   return `${base} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`;
    case 'warn':
    case 'warning': return `${base} bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300`;
    default:        return `${base} bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`;
  }
}

function extractActor(row: AuditEvent): string | undefined {
  return row.actor_email ?? row.user_email ?? row.actor_id ?? row.user_id ?? undefined;
}

// ─── Event type options (grouped for multi-select modal) ───────────────────────

const EVENT_TYPE_GROUPS: { group: string; options: { label: string; value: string }[] }[] = [
  { group: 'File access', options: [
    { label: 'File access', value: 'file_access' },
    { label: 'File created', value: 'file_created' },
    { label: 'File updated', value: 'file_updated' },
    { label: 'File status updated', value: 'file_status_updated' },
    { label: 'File owner changed', value: 'file_owner_changed' },
  ]},
  { group: 'Access requests', options: [
    { label: 'Access request created', value: 'access_request.created' },
    { label: 'Access request accepted', value: 'access_request.accepted' },
    { label: 'Access request rejected', value: 'access_request.rejected' },
  ]},
  { group: 'Auth', options: [
    { label: 'Passkey registered', value: 'user_passkey_registered' },
    { label: 'Passkey removed', value: 'user_passkey_removed' },
  ]},
  { group: 'Users', options: [
    { label: 'User created', value: 'user_user_create' },
    { label: 'User deleted', value: 'deletion.user' },
    { label: 'Role assigned', value: 'user_role_assigned' },
  ]},
  { group: 'Invitations', options: [
    { label: 'Invitation sent', value: 'user_invitation_sent' },
    { label: 'Invitation accepted', value: 'user_invitation_accepted' },
    { label: 'Invitation revoked', value: 'user_invitation_revoked' },
  ]},
  { group: 'Policies', options: [
    { label: 'Policy created', value: 'user_policy_create' },
    { label: 'Policy updated', value: 'user_policy_update' },
    { label: 'Policy deleted', value: 'user_policy_delete' },
    { label: 'Policy recipients add', value: 'user_policy_recipients_add' },
    { label: 'Policy recipients update', value: 'user_policy_recipients_update' },
    { label: 'Policy recipients remove', value: 'user_policy_recipients_remove' },
  ]},
  { group: 'Files & assets', options: [
    { label: 'File deleted', value: 'deletion.file' },
    { label: 'Recipient added', value: 'user_asset_recipients_add' },
    { label: 'Recipient removed', value: 'user_asset_recipients_remove' },
  ]},
  { group: 'Devices', options: [
    { label: 'Device registered', value: 'user_device_add' },
    { label: 'Device revoked', value: 'user_device_revoke' },
  ]},
  { group: 'Company', options: [
    { label: 'Company created', value: 'user_company_create' },
    { label: 'Company deleted', value: 'deletion.company' },
    { label: 'Company updated/suspended', value: 'user_company_update' },
    { label: 'Security settings', value: 'admin_company_security_settings_update' },
    { label: 'Policy settings', value: 'admin_company_policy_settings_update' },
    { label: 'Device settings', value: 'admin_company_device_settings_update' },
    { label: 'Password settings', value: 'admin_company_password_settings_update' },
    { label: 'Notif settings', value: 'admin_company_notif_settings_update' },
    { label: 'Asset protection', value: 'admin_company_asset_protection_update' },
  ]},
  { group: 'Resources', options: [
    { label: 'Tenant DB created', value: 'system_tenant_db_created' },
    { label: 'Tenant DB deleted', value: 'system_tenant_db_deleted' },
  ]},
];

const ALL_EVENT_TYPE_VALUES = EVENT_TYPE_GROUPS.flatMap(g => g.options.map(o => o.value));

// ─── Row detail expand ────────────────────────────────────────────────────────

const ExpandedRow: React.FC<{ event: AuditEvent }> = ({ event }) => (
  <tr className="bg-gray-50 dark:bg-gray-900/60">
    <td colSpan={6} className="px-4 py-3">
      <pre className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-all font-mono bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3 overflow-x-auto max-h-96">
        {JSON.stringify(event, null, 2)}
      </pre>
    </td>
  </tr>
);

// ─── Actor filter dropdown ────────────────────────────────────────────────────

const MIN_SEARCH_CHARS = 3;

interface ActorFilterProps {
  actorFilters: string[];
  onToggle: (actor: string) => void;
  suggestRows: AuditEvent[];
}

const ActorFilterDropdown: React.FC<ActorFilterProps> = ({ actorFilters, onToggle, suggestRows }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const allActors = useMemo(() => {
    const set = new Set<string>();
    for (const row of suggestRows) {
      const a = extractActor(row);
      if (a) set.add(a);
    }
    return Array.from(set).sort();
  }, [suggestRows]);

  const hasEnough = search.length >= MIN_SEARCH_CHARS;
  const visible = hasEnough
    ? allActors.filter(a => a.toLowerCase().includes(search.toLowerCase()))
    : [];

  return (
    <div className="flex flex-col gap-1 relative" ref={containerRef}>
      <label className="text-xs text-gray-500 dark:text-gray-400">Actor</label>
      <input
        type="text"
        placeholder="Type to search…"
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white w-40"
      />
      {open && (
        <div className="absolute top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg w-full min-w-max max-h-52 overflow-y-auto">
          {!hasEnough ? (
            <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">Type at least {MIN_SEARCH_CHARS} characters…</p>
          ) : visible.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">No actors found</p>
          ) : (
            visible.map(a => (
              <button
                key={a}
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onToggle(a); setSearch(''); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between gap-4"
              >
                <span className="truncate">{a}</span>
                {actorFilters.includes(a) && <span className="text-indigo-600 dark:text-indigo-400 shrink-0">✓</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── Timeline slider ──────────────────────────────────────────────────────────

interface TimelineProps {
  fromMs: number;
  toMs: number;
  sliderFrom: number; // 0–1000
  sliderTo: number;   // 0–1000
  onFromChange: (v: number) => void;
  onToChange: (v: number) => void;
}

const Timeline: React.FC<TimelineProps> = ({ fromMs, toMs, sliderFrom, sliderTo, onFromChange, onToChange }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<'from' | 'to' | null>(null);

  // Use a ref to always have fresh values in the pointermove handler without re-registering
  const valRef = useRef({ sliderFrom, sliderTo, onFromChange, onToChange });
  valRef.current = { sliderFrom, sliderTo, onFromChange, onToChange };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current || !trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const raw = Math.max(0, Math.min(1000, Math.round(((e.clientX - rect.left) / rect.width) * 1000)));
      const { sliderFrom, sliderTo, onFromChange, onToChange } = valRef.current;
      if (draggingRef.current === 'from') {
        onFromChange(Math.min(raw, sliderTo - 10));
      } else {
        onToChange(Math.max(raw, sliderFrom + 10));
      }
    };
    const onUp = () => { draggingRef.current = null; };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
  }, []);

  const msAt = (v: number) => fromMs + (v / 1000) * (toMs - fromMs);
  const fromPct = (sliderFrom / 1000) * 100;
  const toPct = (sliderTo / 1000) * 100;

  return (
    <div
      className="fixed bottom-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-3 z-30 transition-[left] duration-300"
      style={{ left: 'var(--sidebar-width, 0px)' }}
    >
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
        {/* Timestamps: above track on mobile, sides on desktop */}
        <div className="flex items-center justify-between sm:hidden gap-2">
          <span className="text-[10px] font-mono text-gray-500 dark:text-gray-400 shrink-0">{fmtMs(msAt(sliderFrom))}</span>
          <span className="text-gray-300 dark:text-gray-600 shrink-0">|</span>
          <span className="text-[10px] font-mono text-gray-500 dark:text-gray-400 shrink-0">{fmtMs(msAt(sliderTo))}</span>
        </div>

        <span className="hidden sm:block text-xs font-mono text-gray-500 dark:text-gray-400 shrink-0 text-right w-40">
          {fmtMs(msAt(sliderFrom))}
        </span>

        {/* Track */}
        <div
          ref={trackRef}
          className="relative w-full sm:flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full select-none"
        >
          {/* Highlighted range */}
          <div
            className="absolute top-0 h-full bg-indigo-400 dark:bg-indigo-600 rounded-full pointer-events-none"
            style={{ left: `${fromPct}%`, width: `${toPct - fromPct}%` }}
          />
          {/* From handle */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white dark:bg-gray-200 border-2 border-indigo-500 shadow cursor-grab active:cursor-grabbing"
            style={{ left: `${fromPct}%` }}
            onPointerDown={() => { draggingRef.current = 'from'; }}
          />
          {/* To handle */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white dark:bg-gray-200 border-2 border-indigo-500 shadow cursor-grab active:cursor-grabbing"
            style={{ left: `${toPct}%` }}
            onPointerDown={() => { draggingRef.current = 'to'; }}
          />
        </div>

        <span className="hidden sm:block text-xs font-mono text-gray-500 dark:text-gray-400 shrink-0 w-40">
          {fmtMs(msAt(sliderTo))}
        </span>
      </div>
    </div>
  );
};

// ─── Event type multi-select modal ─────────────────────────────────────────────

interface EventTypeModalProps {
  selected: string[];
  onSelect: (values: string[]) => void;
  onClose: () => void;
  onApply: () => void;
}

const EventTypeModal: React.FC<EventTypeModalProps> = ({ selected, onSelect, onClose, onApply }) => {
  const [local, setLocal] = useState<string[]>(selected);
  const selectAll = () => setLocal([...ALL_EVENT_TYPE_VALUES]);
  const deselectAll = () => setLocal([]);
  const toggle = (value: string) => {
    setLocal(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Event type</h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-2 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800">
          <button type="button" onClick={deselectAll} className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            Deselect all
          </button>
          <button type="button" onClick={selectAll} className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline">
            Select all
          </button>
          <span className="text-xs text-gray-400 dark:text-gray-500">{local.length} selected</span>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {EVENT_TYPE_GROUPS.map(({ group, options }) => (
              <div key={group} className="space-y-2">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider pb-1 border-b border-gray-100 dark:border-gray-800">
                  {group}
                </div>
                <ul className="space-y-1.5">
                  {options.map(({ label, value }) => (
                    <li key={value}>
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={local.includes(value)}
                          onChange={() => toggle(value)}
                          className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:text-white">{label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { onSelect(local); onApply(); }}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Report modal (export filtered results) ────────────────────────────────────
// TODO(export): Re-enable when backend export returns correct number of logs (filtered results).
// Currently disabled: Get report button and modal usage are commented out above.
interface ReportModalProps {
  from: string;
  to: string;
  eventTypes: string[];
  onClose: () => void;
  systemScope: boolean;
}

// TODO(export): Used when Get report is re-enabled; export must return correct filtered count.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ReportModal: React.FC<ReportModalProps> = ({ from, to, eventTypes, onClose, systemScope }) => {
  const [format, setFormat] = useState<'csv' | 'json' | 'pdf'>('csv');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setError(null);
    setLoading(true);
    try {
      const fromFmt = from.includes('T') ? from : from.replace(' ', 'T') + 'Z';
      const toFmt = to.includes('T') ? to : to.replace(' ', 'T') + 'Z';
      const blob = await apiService.exportAuditEvents({
        from: fromFmt,
        to: toFmt,
        format,
        ...(eventTypes.length > 0 ? { event_types: eventTypes } : {}),
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_report_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (e: unknown) {
      setError((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Get report</h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Export <strong>filtered results only</strong> (current time range and event type filters).
          </p>
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <p><strong>From:</strong> {from}</p>
            <p><strong>To:</strong> {to}</p>
            <p><strong>Event types:</strong> {eventTypes.length === 0 ? 'All' : eventTypes.length + ' selected'}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Format</label>
            <div className="flex gap-3">
              {(['csv', 'json', 'pdf'] as const).map(f => (
                <label key={f} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="format" checked={format === f} onChange={() => setFormat(f)} className="text-indigo-600 focus:ring-indigo-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300 uppercase">{f}</span>
                </label>
              ))}
            </div>
          </div>
          {error && <p className="text-sm text-red-500 dark:text-red-400">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-2"
          >
            {loading ? (
              <>
                <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                Generating…
              </>
            ) : (
              'Generate'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

interface AuditsProps {
  systemScope?: boolean;
}

const Audits: React.FC<AuditsProps> = ({ systemScope = false }) => {
  usePageTitle(systemScope ? 'System Logs' : 'Logs');
  const { user } = useAuth();

  // Admin/sysadmin users can view personal logs when in user-mode sidebar.
  const isAdminRole = user?.role === 'admin' || user?.role === 'sysadmin';
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('view_mode') ?? 'user');
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.viewMode) setViewMode(detail.viewMode);
    };
    window.addEventListener('view-mode-changed', handler);
    return () => window.removeEventListener('view-mode-changed', handler);
  }, []);
  const userScope = isAdminRole && viewMode === 'user';

  const [searchParams, setSearchParams] = useSearchParams();

  // Filters
  const [timeRangeAmount, setTimeRangeAmount] = useState(24);
  const [timeRangeUnit, setTimeRangeUnit] = useState<TimeRangeUnit>('hours');
  const [refreshEpoch, setRefreshEpoch] = useState(0);
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [showEventTypeModal, setShowEventTypeModal] = useState(false);
  // TODO(export): Re-enable when export returns correct number of logs (filtered results). See ReportModal below.
  // const [showReportModal, setShowReportModal] = useState(false);
  const [search, setSearch] = useState(() => searchParams.get('resource_id') ?? '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('resource_id') ?? '');
  const [actorFilters, setActorFilters] = useState<string[]>([]);
  const [resourceFilter, setResourceFilter] = useState<{ id: string; name: string } | null>(null);
  const [companyIdFilter, setCompanyIdFilter] = useState('');

  // If we arrived via ?resource_id=..., consume it and clean the URL so navigating
  // back to /logs later doesn't carry the stale search.
  useEffect(() => {
    if (searchParams.get('resource_id')) {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Timeline client-side filter (0–1000)
  const [sliderFrom, setSliderFrom] = useState(0);
  const [sliderTo, setSliderTo] = useState(1000);

  const { from: fromDate, to: toDate } = useMemo(
    () => getTimeRangeBounds(timeRangeAmount, timeRangeUnit),
    [timeRangeAmount, timeRangeUnit, refreshEpoch] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Reset timeline sliders when the time window changes
  useEffect(() => {
    setSliderFrom(0);
    setSliderTo(1000);
  }, [fromDate, toDate]);

  const fromMs = useMemo(() => new Date(fromDate.replace(' ', 'T') + 'Z').getTime(), [fromDate]);
  const toMs   = useMemo(() => new Date(toDate.replace(' ', 'T')   + 'Z').getTime(), [toDate]);

  const [displayedPageCount, setDisplayedPageCount] = useState(1);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const baseParams: Omit<AuditQueryParams, 'before'> = {
    ...(eventTypes.length > 0 ? { event_types: eventTypes } : {}),
    search:       search || undefined,
    actor_filter: actorFilters.length > 0 ? actorFilters.join(',') : undefined,
    resource_id:  resourceFilter?.id,
    from: fromDate,
    to:   toDate,
    ...(systemScope && companyIdFilter ? { company_id: companyIdFilter } : {}),
    ...(userScope ? { scope: 'user' as const } : {}),
  };

  // Suggestion params — no actor_filter so all actors in the time window are visible
  const suggestParams: AuditQueryParams = {
    ...(eventTypes.length > 0 ? { event_types: eventTypes } : {}),
    search:     search || undefined,
    from: fromDate,
    to:   toDate,
    ...(systemScope && companyIdFilter ? { company_id: companyIdFilter } : {}),
  };

  const { data, isLoading, isFetching, isFetchingNextPage, error, refetch, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['audit-events', systemScope, baseParams],
      queryFn: async ({ pageParam }: { pageParam?: string }) => {
        const params: AuditQueryParams = { ...baseParams, before: pageParam };
        return systemScope ? apiService.getSystemAuditEvents(params) : apiService.getAuditEvents(params);
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage: AuditEvent[]) =>
        lastPage.length >= AUDIT_PAGE_LIMIT && lastPage.length > 0 ? lastPage[lastPage.length - 1].dt : undefined,
      staleTime: 30_000,
    });

  const { data: suggestData } = useQuery({
    queryKey: ['audit-actor-suggest', systemScope, suggestParams],
    queryFn: () => systemScope ? apiService.getSystemAuditEvents(suggestParams) : apiService.getAuditEvents(suggestParams),
    staleTime: 60_000,
  });

  const pages = data?.pages ?? [];
  const rows  = pages.slice(0, displayedPageCount).flatMap(p => p);

  // Client-side timeline filter
  const visibleRows = useMemo(() => {
    if (sliderFrom === 0 && sliderTo === 1000) return rows;
    const clientFromMs = fromMs + (sliderFrom / 1000) * (toMs - fromMs);
    const clientToMs   = fromMs + (sliderTo   / 1000) * (toMs - fromMs);
    return rows.filter(row => {
      const ms = dtToMs(row.dt);
      return ms >= clientFromMs && ms <= clientToMs;
    });
  }, [rows, sliderFrom, sliderTo, fromMs, toMs]);

  useEffect(() => {
    if (pages.length > 0 && displayedPageCount > pages.length) setDisplayedPageCount(pages.length);
  }, [pages.length, displayedPageCount]);

  const resetPage = () => { setDisplayedPageCount(1); setExpanded(new Set()); };

  const applyFilters = useCallback(() => { setSearch(searchInput); resetPage(); }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') applyFilters(); };

  const toggleActorFilter = (actor: string) => {
    setActorFilters(prev => prev.includes(actor) ? prev.filter(a => a !== actor) : [...prev, actor]);
    resetPage();
  };

  const handleResourceClick = (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setResourceFilter(prev => prev?.id === id ? null : { id, name });
    resetPage();
  };

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage().then(() => setDisplayedPageCount(d => d + 1));
      setExpanded(new Set());
    }
  };
  const loadPrev = () => { if (displayedPageCount > 1) { setDisplayedPageCount(d => d - 1); setExpanded(new Set()); } };
  const toggleExpand = (idx: number) => {
    setExpanded(prev => { const next = new Set(prev); next.has(idx) ? next.delete(idx) : next.add(idx); return next; });
  };

  const hasActiveFilters = actorFilters.length > 0 || resourceFilter !== null || eventTypes.length > 0;

  return (
    <div className="p-4 sm:p-6 max-w-full pb-16">
      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap gap-2 items-end">
        {/* Time range */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 dark:text-gray-400">Time range</label>
          <div className="flex gap-1">
            <select
              value={timeRangeAmount}
              onChange={e => { setTimeRangeAmount(Number(e.target.value)); resetPage(); }}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white w-[72px]"
              aria-label="Time range amount"
            >
              {TIME_RANGE_AMOUNTS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <select
              value={timeRangeUnit}
              onChange={e => { setTimeRangeUnit(e.target.value as TimeRangeUnit); resetPage(); }}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-[110px]"
              aria-label="Time range unit"
            >
              {TIME_RANGE_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
        </div>

        {/* Event type (multi-select modal) */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 dark:text-gray-400">Event type</label>
          <button
            type="button"
            onClick={() => setShowEventTypeModal(true)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 min-w-[180px] text-left flex items-center justify-between gap-2"
          >
            <span className="truncate">
              {eventTypes.length === 0 ? 'All events' : `${eventTypes.length} selected`}
            </span>
            <ChevronDownIcon className="w-4 h-4 shrink-0 text-gray-400" />
          </button>
        </div>

        {/* TODO(export): Re-enable when export returns correct number of logs. Export currently does not match filtered result count. */}
        {/* Get report
        <button
          type="button"
          onClick={() => setShowReportModal(true)}
          className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm self-end"
        >
          <DocumentArrowDownIcon className="w-4 h-4" />
          Get report
        </button>
        */}

        {/* Company ID (system scope) */}
        {systemScope && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Company ID</label>
            <input
              type="text"
              placeholder="filter by company..."
              value={companyIdFilter}
              onChange={e => { setCompanyIdFilter(e.target.value); resetPage(); }}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white w-52"
            />
          </div>
        )}

        {/* Actor filter */}
        <ActorFilterDropdown
          actorFilters={actorFilters}
          onToggle={toggleActorFilter}
          suggestRows={suggestData ?? []}
        />

        {/* Search */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 dark:text-gray-400">Search</label>
          <div className="flex gap-1">
            <input
              type="text"
              placeholder="message or resource name..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white w-56"
            />
            <button
              onClick={applyFilters}
              className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
            >
              <FunnelIcon className="w-4 h-4" />
              Apply
            </button>
          </div>
        </div>

        {/* Refresh */}
        <button
          onClick={() => { setDisplayedPageCount(1); setRefreshEpoch(e => e + 1); refetch(); }}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white self-end pb-1.5"
        >
          <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Active filter pills */}
      {hasActiveFilters && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          {eventTypes.length > 0 && (
            <span className="inline-flex items-center gap-1 text-xs bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 px-2 py-1 rounded-full font-medium">
              Events: {eventTypes.length} type{eventTypes.length !== 1 ? 's' : ''}
              <button onClick={() => { setEventTypes([]); resetPage(); }} className="ml-0.5 hover:text-violet-900 dark:hover:text-violet-100">×</button>
            </span>
          )}
          {actorFilters.map(a => (
            <span key={a} className="inline-flex items-center gap-1 text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded-full font-medium">
              Actor: {a}
              <button onClick={() => toggleActorFilter(a)} className="ml-0.5 hover:text-indigo-900 dark:hover:text-indigo-100">×</button>
            </span>
          ))}
          {resourceFilter && (
            <span className="inline-flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-1 rounded-full font-medium">
              File: {resourceFilter.name || resourceFilter.id}
              <button onClick={() => { setResourceFilter(null); resetPage(); }} className="ml-0.5 hover:text-amber-900 dark:hover:text-amber-100">×</button>
            </span>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900 shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : error ? (
          <div className="px-4 py-10 text-center text-sm text-red-500 dark:text-red-400">
            Failed to load log events. {(error as Error).message}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="w-6 px-2" />
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Time</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Event</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actor</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Resource</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                      No log events found
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row, idx) => (
                    <React.Fragment key={idx}>
                      <tr
                        onClick={() => toggleExpand(idx)}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                      >
                        <td className="px-2 py-2 text-gray-400">
                          {expanded.has(idx) ? <ChevronDownIcon className="w-3.5 h-3.5" /> : <ChevronRightIcon className="w-3.5 h-3.5" />}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap font-mono text-xs">
                          {fmtDt(row.dt)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.event_type ? (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                const et = row.event_type!;
                                setEventTypes(prev => prev.includes(et) ? prev.filter(x => x !== et) : [...prev, et]);
                                resetPage();
                              }}
                              title="Toggle this event type in filter"
                              className={levelBadge(row.level) + ' cursor-pointer hover:opacity-75 transition-opacity'}
                            >
                              {row.event_type}
                            </button>
                          ) : '—'}
                        </td>
                        {/* Actor — click to filter */}
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300 text-xs whitespace-nowrap">
                          {(() => {
                            const actor = extractActor(row);
                            if (!actor) return '—';
                            return (
                              <button
                                onClick={e => { e.stopPropagation(); toggleActorFilter(actor); }}
                                title={actorFilters.includes(actor) ? 'Remove from filter' : 'Filter by this actor'}
                                className="hover:underline hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors text-left"
                              >
                                {actor}
                              </button>
                            );
                          })()}
                        </td>
                        {/* Resource — click to filter (file resources only) */}
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                          {row.resource_type ? (() => {
                            const label = `${row.resource_type}${row.resource_name ? ` / ${row.resource_name}` : ''}`;
                            const rid = row.resource_id as string | undefined;
                            if (row.resource_type === 'file' && rid) {
                              return (
                                <button
                                  onClick={e => handleResourceClick(rid, row.resource_name ?? rid, e)}
                                  title={resourceFilter?.id === rid ? 'Clear file filter' : 'Filter by this file'}
                                  className="hover:underline hover:text-amber-600 dark:hover:text-amber-400 transition-colors text-left"
                                >
                                  {label}
                                </button>
                              );
                            }
                            return <span>{label}</span>;
                          })() : '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300 text-xs max-w-xs truncate">
                          {row.message ?? '—'}
                        </td>
                      </tr>
                      {expanded.has(idx) && <ExpandedRow event={row} />}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && (
        <div className="mt-3 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <button
            onClick={loadPrev}
            disabled={displayedPageCount <= 1}
            className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            ← Previous
          </button>
          <span className="text-xs">
            {visibleRows.length} rows{displayedPageCount > 1 ? ` · page ${displayedPageCount} of ${pages.length}` : ''}
            {(sliderFrom > 0 || sliderTo < 1000) && ` (timeline filtered from ${rows.length})`}
          </span>
          <button
            onClick={loadMore}
            disabled={!hasNextPage || isFetchingNextPage}
            className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more →'}
          </button>
        </div>
      )}

      {/* Fixed timeline at bottom */}
      <Timeline
        fromMs={fromMs}
        toMs={toMs}
        sliderFrom={sliderFrom}
        sliderTo={sliderTo}
        onFromChange={setSliderFrom}
        onToChange={setSliderTo}
      />

      {/* Event type multi-select modal */}
      {showEventTypeModal && (
        <EventTypeModal
          selected={eventTypes}
          onSelect={setEventTypes}
          onClose={() => setShowEventTypeModal(false)}
          onApply={() => { resetPage(); setShowEventTypeModal(false); }}
        />
      )}

      {/* TODO(export): Re-enable ReportModal when export returns correct number of logs (filtered results). */}
      {/* Get report modal
      {showReportModal && (
        <ReportModal
          from={fromDate}
          to={toDate}
          eventTypes={eventTypes}
          onClose={() => setShowReportModal(false)}
          systemScope={systemScope}
        />
      )}
      */}
    </div>
  );
};

export default Audits;
